import { z } from 'zod';
import { isValidEmail } from '../email.js';
import { toIssues } from '../materials/catalog.js';
import { SUPPLIER_TYPES } from '../purchasing/supplier.js';
import type { ParseResult } from '../voyage/schemas.js';
import { SERVICE_REGIONS } from './regions.js';

export {
  isServiceRegion,
  REGION_COUNTRIES,
  regionOfCountry,
  SERVICE_REGIONS,
  type ServiceRegion,
  supplierServesRegion,
} from './regions.js';

/**
 * Açık RFQ pazar yeri ve tedarikçinin kendi kaydı (ADR 0113; sahip kararı 2026-10-23: "açık da
 * olabilir girip kayıt oluşturup bakar yada supliyer seçer ordaki bölgede seçebilir sadece onlar
 * teklif verebilir"). Bölgeler `regions.ts`'tedir. Hata iletileri koddur.
 */

/**
 * Tedarik kategorileri (IMPA ana gruplarına yakın, kaba): tedarikçi kayıtta seçer; ofiste bilgi
 * ve arama içindir, görünürlüğe karışmaz.
 */
export const SUPPLY_CATEGORIES = [
  'provisions',
  'bonded_stores',
  'deck_cabin_stores',
  'engine_spares',
  'electrical',
  'safety_fire',
  'chemicals_lubricants',
  'medical',
  'nautical_charts',
  'tools_hardware',
  'services_repairs',
  'logistics_agency',
] as const;
export type SupplyCategory = (typeof SUPPLY_CATEGORIES)[number];

/** Kayıtta en çok bu kadar liman adı (bilgi ve arama içindir). */
export const MAX_SERVICE_PORTS = 10;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (min: number, max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').min(min, 'TOO_SHORT').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const unique = <T>(values: readonly T[]) => [...new Set(values)];
const codes = <T extends readonly [string, ...string[]]>(values: T, max: number) =>
  z
    .array(z.enum(values, typed('INVALID_OPTION')), typed('INVALID_OPTION'))
    .min(1, 'REQUIRED')
    .max(max, 'TOO_MANY')
    .transform(unique);

export const supplierProfileSchema = z.strictObject({
  companyName: text(2, 200),
  supplierType: z.enum(SUPPLIER_TYPES, typed('INVALID_OPTION')),
  /** Şirketin ülkesi, ISO 3166-1 alfa-2. */
  country: z
    .string(typed('INVALID_COUNTRY'))
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{2}$/, 'INVALID_COUNTRY')),
  regions: codes(SERVICE_REGIONS, SERVICE_REGIONS.length),
  categories: codes(SUPPLY_CATEGORIES, SUPPLY_CATEGORIES.length),
  ports: z
    .array(text(1, 60), typed('INVALID_TEXT'))
    .max(MAX_SERVICE_PORTS, 'TOO_MANY')
    .nullish()
    .transform((value) => unique(value ?? [])),
  phone: optionalText(40),
});
export type SupplierProfile = z.output<typeof supplierProfileSchema>;

export function parseSupplierProfile(input: unknown): ParseResult<SupplierProfile> {
  const result = supplierProfileSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Kendi kaydı (ADR 0113 §1): profil + kişi, giriş e-postası, parola (kuralı sunucuda, ADR 0005),
 * dil ve kullanım koşullarının onayı. Bilinmeyen alan reddedilir (`strictObject`): onay, bağ ya
 * da durum gibi alanlar istekten gelmez.
 */
export const supplierRegistrationSchema = supplierProfileSchema.extend({
  fullName: text(1, 120),
  email: z
    .string(typed('INVALID_EMAIL'))
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.string().max(320, 'TOO_LONG'))
    .refine(isValidEmail, 'INVALID_EMAIL'),
  password: z.string(typed('INVALID_TEXT')).min(1, 'REQUIRED').max(1024, 'TOO_LONG'),
  language: z.enum(['en', 'tr'], typed('INVALID_OPTION')),
  acceptTerms: z.boolean(typed('INVALID_BOOLEAN')),
});
export type SupplierRegistration = z.output<typeof supplierRegistrationSchema>;

export function parseSupplierRegistration(input: unknown): ParseResult<SupplierRegistration> {
  const result = supplierRegistrationSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Açık RFQ şirkete bağlı olmayan tedarikçiye görünür mü (sahip kararı 2026-10-23; ADR 0113 §2):
 * yayımlanmış ve "açık". Bütün doğrulanmış, etkin hesaplara açıktır; bölge görünürlük kapısı
 * değildir, yalnız tedarikçinin listesinde süzgeçtir. Hesabın doğrulanmış ve etkin olduğu, RFQ'nun
 * kapanmadığı çağıranda denetlenir.
 */
export function openRequestVisible(rfq: {
  publishedAt: string | null;
  visibility: string | null;
}): boolean {
  return rfq.publishedAt !== null && rfq.visibility === 'open';
}
